import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bot as BotIcon, MoreVertical, Power, Search } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import {
  useBotsList,
  useBotPlatform,
  useSetBotPlatform,
  useDisableBot,
  useEnableBot,
  useDeleteBot,
} from '../../hooks/queries/useBotsQueries';
import { AdminBot } from '../../types/bots';
import { formatDateTime } from '../../utils/formatDate';
import { getImageUrl } from '../../utils/imageUrl';
import Pagination from '../Pagination';
import Spinner from '../ui/spinner';
import BotStatusBadge from './BotStatusBadge';
import BotWebhookBadge from './BotWebhookBadge';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PAGE_SIZE = 20;

type EStatusFilter = 'all' | 'active' | 'disabled';
type EWebhookFilter =
  'all' | 'healthy' | 'failing' | 'autoDisabled' | 'polling';

// `healthy` is a webhook configured with no fail streak; `failing` is now its
// own AdminBotsQueryDto.webhookState value (configured, not auto-disabled,
// but erroring) rather than reusing `configured` for both.
const WEBHOOK_FILTER_TO_API: Record<
  EWebhookFilter,
  'configured' | 'disabled' | 'none' | 'failing' | undefined
> = {
  all: undefined,
  healthy: 'configured',
  failing: 'failing',
  autoDisabled: 'disabled',
  polling: 'none',
};

const shortId = (id: string) => `${id.slice(0, 8)}…`;

interface IBotListViewProps {
  onNavigateBot?: (botId: string) => void;
}

/**
 * Backoffice "Bots" list screen: platform kill switch + a filterable,
 * paginated table of every bot across every space. Mirrors the structure of
 * AdminListView / UserManagement (search + status chips + table + pagination).
 */
const BotListView = ({ onNavigateBot }: IBotListViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirmDialog = useConfirmDialog();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const statusFilter = (searchParams.get('status') as EStatusFilter) || 'all';
  const webhookFilter =
    (searchParams.get('webhook') as EWebhookFilter) || 'all';
  const page = Number(searchParams.get('page')) || 1;

  const debouncedSearch = useDebouncedValue(search, 300);

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== 'all') {
          next.set(key, value);
        } else {
          next.delete(key);
        }

        next.delete('page');

        return next;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (newPage > 1) {
          next.set('page', String(newPage));
        } else {
          next.delete('page');
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const { data: platform } = useBotPlatform();
  const setPlatform = useSetBotPlatform();

  const { data, isLoading } = useBotsList({
    page,
    limit: PAGE_SIZE,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    webhookState: WEBHOOK_FILTER_TO_API[webhookFilter],
    search: debouncedSearch || undefined,
  });

  const disableBot = useDisableBot();
  const enableBot = useEnableBot();
  const deleteBot = useDeleteBot();

  const bots = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = data?.meta.totalPages ?? 0;

  const handleViewDetail = (bot: AdminBot) => {
    if (onNavigateBot) {
      onNavigateBot(bot.id);
    } else {
      navigate(`/bots/${bot.id}`);
    }
  };

  const handleTogglePlatform = () => {
    const enabled = !!platform?.enabled;

    if (enabled) {
      confirmDialog.show(t('bots.platform.confirmOff'), async () => {
        await setPlatform.mutateAsync({ enabled: false });
      });
    } else {
      setPlatform.mutate({ enabled: true });
    }
  };

  const handleDisable = (bot: AdminBot) => {
    confirmDialog.show(t('bots.confirm.disable'), async () => {
      await disableBot.mutateAsync(bot.id);
    });
  };

  const handleEnable = (bot: AdminBot) => {
    confirmDialog.show(t('bots.confirm.enable'), async () => {
      await enableBot.mutateAsync(bot.id);
    });
  };

  const handleDelete = (bot: AdminBot) => {
    confirmDialog.show(t('bots.confirm.delete'), async () => {
      await deleteBot.mutateAsync(bot.id);
    });
  };

  const statusFilters: { key: EStatusFilter; label: string }[] = [
    { key: 'all', label: t('bots.filters.all') },
    { key: 'active', label: t('bots.status.active') },
    { key: 'disabled', label: t('bots.status.disabled') },
  ];

  const webhookFilters: { key: EWebhookFilter; label: string }[] = [
    { key: 'all', label: t('bots.filters.all') },
    { key: 'healthy', label: t('bots.webhook.healthy') },
    { key: 'failing', label: t('bots.webhook.failing') },
    { key: 'autoDisabled', label: t('bots.webhook.autoDisabled') },
    { key: 'polling', label: t('bots.webhook.polling') },
  ];

  return (
    <div className="p-4 lg:p-8 space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('bots.title')}</h1>
        <p className="text-base text-gray-600 mt-2">{t('bots.subtitle')}</p>
      </div>

      {/* Platform kill switch */}
      <Card>
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Power className="w-8 h-8 text-blue-600" />
            <div>
              <div className="text-sm font-medium text-foreground">
                {t('bots.platform.title')}
              </div>
              <Badge
                variant={platform?.enabled ? 'success' : 'muted'}
                className="mt-1"
              >
                {platform?.enabled
                  ? t('bots.platform.enabled')
                  : t('bots.platform.disabled')}
              </Badge>
            </div>
          </div>
          <Button
            variant={platform?.enabled ? 'destructive' : 'default'}
            onClick={handleTogglePlatform}
            disabled={setPlatform.isPending}
          >
            {platform?.enabled
              ? t('bots.platform.toggleOff')
              : t('bots.platform.toggleOn')}
          </Button>
        </CardContent>
      </Card>

      {/* Search & filters */}
      <Card className="p-4 gap-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Search className="w-5 h-5" />
            </span>
            <Input
              placeholder={t('bots.filters.search')}
              value={search}
              onChange={(e) => setParam('search', e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-1">
              {t('bots.filters.status')}
            </span>
            {statusFilters.map(({ key, label }) => (
              <Button
                key={key}
                variant={statusFilter === key ? 'default' : 'outline'}
                onClick={() => setParam('status', key)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">
            {t('bots.filters.webhook')}
          </span>
          {webhookFilters.map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              variant={webhookFilter === key ? 'default' : 'outline'}
              onClick={() => setParam('webhook', key)}
            >
              {label}
            </Button>
          ))}
        </div>
      </Card>

      {/* Table */}
      {isLoading ? (
        <Spinner />
      ) : (
        <Card className="gap-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('bots.columns.bot')}</TableHead>
                <TableHead>{t('bots.columns.space')}</TableHead>
                <TableHead>{t('bots.columns.owner')}</TableHead>
                <TableHead>{t('bots.columns.status')}</TableHead>
                <TableHead>{t('bots.columns.webhook')}</TableHead>
                <TableHead>{t('bots.columns.created')}</TableHead>
                <TableHead className="w-10 text-center">
                  {t('bots.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bots.map((bot) => (
                <TableRow
                  key={bot.id}
                  onClick={() => handleViewDetail(bot)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm overflow-hidden shrink-0">
                        {bot.avatar ? (
                          <img
                            src={getImageUrl(bot.avatar)!}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <BotIcon className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {bot.displayName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          @{bot.username}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-muted-foreground"
                    title={bot.spaceId}
                  >
                    {bot.space?.name ?? shortId(bot.spaceId)}
                  </TableCell>
                  <TableCell
                    className="text-sm text-muted-foreground"
                    title={bot.ownerUserId}
                  >
                    {bot.owner ? (
                      <>
                        <div className="text-foreground">
                          {bot.owner.displayName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          @{bot.owner.username}
                        </div>
                      </>
                    ) : (
                      shortId(bot.ownerUserId)
                    )}
                  </TableCell>
                  <TableCell>
                    <BotStatusBadge status={bot.status} />
                  </TableCell>
                  <TableCell>
                    <BotWebhookBadge bot={bot} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDateTime(bot.createdAt)}
                  </TableCell>
                  <TableCell
                    className="text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetail(bot)}>
                          {t('bots.actions.view')}
                        </DropdownMenuItem>
                        {bot.status === 'active' ? (
                          <DropdownMenuItem onClick={() => handleDisable(bot)}>
                            {t('bots.actions.disable')}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleEnable(bot)}>
                            {t('bots.actions.enable')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => handleDelete(bot)}
                        >
                          {t('bots.actions.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {bots.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    {t('bots.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={PAGE_SIZE}
            onPageChange={setPage}
            testId="BotManagement_List"
          />
        </Card>
      )}

      {/* Shared confirmation dialog: platform toggle-off, disable, enable, delete */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => !open && confirmDialog.cancel()}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={confirmDialog.cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDialog.confirm}
              disabled={confirmDialog.isLoading}
            >
              {confirmDialog.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BotListView;
